/**
 * AI Filter Actions Service
 * Handles AI-compatible filter actions for Hospital, Community Transfer, and Deceased pages
 * Supports applying filters to DC/ER, Doctors, Insurance, Floors, etc.
 */

import { HOSPITAL_CARDS_TYPE, HOSPITAL_CARDS_LABELS } from '../types/hospital.type';
import { CO_TRANSFER_CARDS_TYPE, COMMUNITY_CARD_LABELS } from '../types/community-transfer.type';
import { DECEASED_CARDS_TYPE, DECEASED_CARDS_LABELS } from '../types/deceased.type';
import logger from '../utilis/logger';

class AiFilterActionsService {
    constructor() {
        this.dispatch = null;
        this.getState = null;
        this.handleToggle = null;
        this.handleToggleAll = null;
        this.onFilterApplied = null;
        this.pageType = null; // 'hospital', 'communityTransfer', or 'deceased'
    }

    /**
     * Initialize the service with required functions
     * @param {Function} dispatch - Redux dispatch function
     * @param {Function} getState - Function to get current Redux state
     * @param {Function} handleToggle - Card item toggle function
     * @param {Function} handleToggleAll - Card toggle all function
     * @param {Function} onFilterApplied - Callback when filter is applied
     */
    initialize(dispatch, getState, handleToggle, handleToggleAll, onFilterApplied = null) {
        this.dispatch = dispatch;
        this.getState = getState;
        this.handleToggle = handleToggle;
        this.handleToggleAll = handleToggleAll;
        this.onFilterApplied = onFilterApplied;
        
        // Detect page type from state structure
        const state = getState();
        if (state.hospital) {
            this.pageType = 'hospital';
        } else if (state.communityTransfer) {
            this.pageType = 'communityTransfer';
        } else if (state.deceased) {
            this.pageType = 'deceased';
        }
        
        logger.log('🔧 [Filter Service] AI Filter Actions Service initialized for:', this.pageType);
        logger.log('🔍 [Filter Service] State structure:', {
            hasHospital: !!state.hospital,
            hasCommunityTransfer: !!state.communityTransfer,
            hasDeceased: !!state.deceased,
            deceasedStateKeys: state.deceased ? Object.keys(state.deceased) : [],
            hasDynamicCards: !!(state.deceased?.dynamicCards),
            hasPatientList: !!(state.deceased?.patientList),
            hasCustomTabs: !!(state.deceased?.patientList?.customTabs)
        });
    }

    /**
     * Get all available filter cards and their items
     * @returns {Object} Available filter options
     */
    getAvailableFilters() {
        if (!this.getState) return {};

        const state = this.getState();
        const pageState = state[this.pageType] || state.hospital || state.communityTransfer || state.deceased || {};
        const { filterListData, cardFilter } = pageState;

        // If this is community transfer page, use community card definitions
        if (this.pageType === 'communityTransfer') {
            return this.getCommunityTransferFilters(filterListData, cardFilter, pageState);
        }

        // If this is deceased page, use deceased card definitions
        if (this.pageType === 'deceased') {
            return this.getDeceasedFilters(filterListData, cardFilter, pageState);
        }

        // Hospital page filters
        const availableFilters = {
            // DC/ER Card
            dcer: {
                type: HOSPITAL_CARDS_TYPE.DCER_DATA,
                label: HOSPITAL_CARDS_LABELS.DCERData,
                items: filterListData?.DCERData || [],
                selectedItems: cardFilter?.DCERData || [],
                aliases: ['dc', 'er', 'dc/er', 'dcer', 'discharge', 'emergency']
            },

            // Doctors Card
            doctors: {
                type: HOSPITAL_CARDS_TYPE.DOCTOR_DATA,
                label: HOSPITAL_CARDS_LABELS.doctorData,
                items: filterListData?.doctorData || [],
                selectedItems: cardFilter?.doctorData || [],
                aliases: ['doctor', 'doctors', 'physician', 'physicians', 'dr', 'doc']
            },

            // Insurance Card
            insurance: {
                type: HOSPITAL_CARDS_TYPE.INSURANCE_DATA,
                label: HOSPITAL_CARDS_LABELS.insuranceData,
                items: filterListData?.insuranceData || [],
                selectedItems: cardFilter?.insuranceData || [],
                aliases: ['insurance', 'insurances', 'payer', 'payers']
            },

            // Floors Card
            floors: {
                type: HOSPITAL_CARDS_TYPE.FLOORS_DATA,
                label: HOSPITAL_CARDS_LABELS.floorsData,
                items: filterListData?.floorsData || [],
                selectedItems: cardFilter?.floorsData || [],
                aliases: ['floor', 'floors', 'unit', 'units', 'ward', 'wards']
            },

            // Hospitalizations Card
            hospitalizations: {
                type: HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS,
                label: HOSPITAL_CARDS_LABELS.hospitalizations,
                items: filterListData?.hospitalizations || [],
                selectedItems: cardFilter?.hospitalizations || [],
                aliases: ['hospitalizations', 'hospitalization', 'new hospitalizations', 're-hospitalizations', 'rehospitalizations']
            },

            // Returns Card
            returns: {
                type: HOSPITAL_CARDS_TYPE.RETURNS_DATA,
                label: HOSPITAL_CARDS_LABELS.returnsData,
                items: filterListData?.returnsData || [],
                selectedItems: cardFilter?.returnsData || [],
                aliases: ['returns', 'returned', 'didnt return', "didn't return", 'return']
            },

            // 90 Days Analysis Card
            ninetyDays: {
                type: HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA,
                label: HOSPITAL_CARDS_LABELS.ninetyDaysData,
                items: filterListData?.ninetyDaysData || [],
                selectedItems: cardFilter?.ninetyDaysData || [],
                aliases: ['90 days', 'ninety days', '90-days', 'days analysis', 'length of stay']
            },

            // Days of Week Card
            days: {
                type: HOSPITAL_CARDS_TYPE.DAYS_DATA,
                label: HOSPITAL_CARDS_LABELS.daysData,
                items: filterListData?.daysData || [],
                selectedItems: cardFilter?.daysData || [],
                aliases: ['days', 'day', 'weekday', 'weekdays', 'day of week', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
            },

            // Diagnosis Card
            diagnosis: {
                type: HOSPITAL_CARDS_TYPE.DX_DATA,
                label: HOSPITAL_CARDS_LABELS.dxData,
                items: filterListData?.dxData || [],
                selectedItems: cardFilter?.dxData || [],
                aliases: ['diagnosis', 'diagnoses', 'dx', 'condition', 'conditions', 'icd']
            },

            // Shift Card
            shifts: {
                type: HOSPITAL_CARDS_TYPE.SHIFT_DATA,
                label: HOSPITAL_CARDS_LABELS.shiftData,
                items: filterListData?.shiftData || [],
                selectedItems: cardFilter?.shiftData || [],
                aliases: ['shift', 'shifts', 'morning', 'evening', 'night', 'day shift', 'night shift']
            },

            // Nurses Card
            nurses: {
                type: HOSPITAL_CARDS_TYPE.NURSE_DATA,
                label: HOSPITAL_CARDS_LABELS.nurseData,
                items: filterListData?.nurseData || [],
                selectedItems: cardFilter?.nurseData || [],
                aliases: ['nurse', 'nurses', 'nursing', 'rn', 'lpn', 'cna']
            },

            // Hospitals Card
            hospitals: {
                type: HOSPITAL_CARDS_TYPE.HOSPITAL_DATA,
                label: HOSPITAL_CARDS_LABELS.hospitalData,
                items: filterListData?.hospitalData || [],
                selectedItems: cardFilter?.hospitalData || [],
                aliases: ['hospital', 'hospitals', 'facility', 'facilities', 'transferred to']
            }
        };

        // CRITICAL: Add custom/dynamic cards
        const dynamicCards = pageState?.dynamicCards || [];
        const customTabs = pageState?.patientList?.customTabs || [];
        
        // Add dynamic cards (question-based custom cards)
        dynamicCards.forEach(card => {
            const accessor = card.accessor;
            const dataList = filterListData?.[accessor];
            if (dataList && dataList.length > 0) {
                availableFilters[accessor] = {
                    type: accessor,
                    label: card.label || card.title || card.tableLabel,
                    items: dataList,
                    selectedItems: cardFilter?.[accessor] || [],
                    aliases: [accessor, (card.label || '').toLowerCase(), (card.title || '').toLowerCase()].filter(Boolean),
                    isCustomCard: true,
                    isCustomTab: false
                };
            }
        });
        
        // Add custom tabs (filter-based custom cards)
        customTabs.forEach(tab => {
            const accessor = tab.accessor;
            const dataList = filterListData?.[accessor];
            if (dataList && dataList.length > 0) {
                availableFilters[accessor] = {
                    type: accessor,
                    label: tab.label || tab.title,
                    items: dataList,
                    selectedItems: cardFilter?.[accessor] || [],
                    aliases: [accessor, (tab.label || '').toLowerCase(), (tab.title || '').toLowerCase()].filter(Boolean),
                    isCustomCard: false,
                    isCustomTab: true
                };
            }
        });

        return availableFilters;
    }

    /**
     * Get Community Transfer page filters
     * @param {Object} filterListData - Filter list data from state
     * @param {Object} cardFilter - Card filter from state
     * @param {Object} pageState - Page state object
     * @returns {Object} Available filter options
     */
    getCommunityTransferFilters(filterListData, cardFilter, pageState) {
        const availableFilters = {
            // Insurance Card
            insurance: {
                type: CO_TRANSFER_CARDS_TYPE.INSURANCE_DATA,
                label: COMMUNITY_CARD_LABELS.insuranceData,
                items: filterListData?.insuranceData || [],
                selectedItems: cardFilter?.insuranceData || [],
                aliases: ['insurance', 'insurances', 'payer', 'payers']
            },

            // 90 Days Analysis (called 60 days in code)
            sixtyDays: {
                type: CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA,
                label: COMMUNITY_CARD_LABELS.sixtyDaysData,
                items: filterListData?.sixtyDaysData || [],
                selectedItems: cardFilter?.sixtyDaysData || [],
                aliases: ['60 days', 'sixty days', '90 days', 'ninety days', 'days analysis', 'length of stay']
            },

            // Returns Card
            returns: {
                type: CO_TRANSFER_CARDS_TYPE.RETURNS_DATA,
                label: COMMUNITY_CARD_LABELS.returnsData,
                items: filterListData?.returnsData || [],
                selectedItems: cardFilter?.returnsData || [],
                aliases: ['returns', 'returned', 'didnt return', "didn't return", 'return']
            },

            // Doctors Card
            doctors: {
                type: CO_TRANSFER_CARDS_TYPE.DOCTOR_DATA,
                label: COMMUNITY_CARD_LABELS.doctorData,
                items: filterListData?.doctorData || [],
                selectedItems: cardFilter?.doctorData || [],
                aliases: ['doctor', 'doctors', 'physician', 'physicians', 'dr', 'doc']
            },

            // Floors Card
            floors: {
                type: CO_TRANSFER_CARDS_TYPE.FLOORS_DATA,
                label: COMMUNITY_CARD_LABELS.floorsData,
                items: filterListData?.floorsData || [],
                selectedItems: cardFilter?.floorsData || [],
                aliases: ['floor', 'floors', 'unit', 'units', 'ward', 'wards']
            },

            // Safe Discharge Assisted Living Card
            safeDischargeAssLiv: {
                type: CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA,
                label: COMMUNITY_CARD_LABELS.safeDischargeAssLivData,
                items: filterListData?.safeDischargeAssLivData || [],
                selectedItems: cardFilter?.safeDischargeAssLivData || [],
                aliases: ['safe discharge', 'assisted living', 'safe discharge assisted living', 'sdal']
            },

            // SNF Facility Card
            snfFacility: {
                type: CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA,
                label: COMMUNITY_CARD_LABELS.snfFacilityData,
                items: filterListData?.snfFacilityData || [],
                selectedItems: cardFilter?.snfFacilityData || [],
                aliases: ['snf', 'snf facility', 'skilled nursing', 'skilled nursing facility']
            },

            // Days of Week Card
            days: {
                type: CO_TRANSFER_CARDS_TYPE.DAYS_DATA,
                label: COMMUNITY_CARD_LABELS.daysData,
                items: filterListData?.daysData || [],
                selectedItems: cardFilter?.daysData || [],
                aliases: ['days', 'day', 'weekday', 'weekdays', 'day of week', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
            }
        };

        // Add custom/dynamic cards
        const dynamicCards = pageState?.dynamicCards || [];
        const customTabs = pageState?.patientList?.customTabs || [];
        
        // Add dynamic cards (question-based custom cards)
        dynamicCards.forEach(card => {
            const accessor = card.accessor;
            const dataList = filterListData?.[accessor];
            if (dataList && dataList.length > 0) {
                availableFilters[accessor] = {
                    type: accessor,
                    label: card.label || card.title || card.tableLabel,
                    items: dataList,
                    selectedItems: cardFilter?.[accessor] || [],
                    aliases: [accessor, (card.label || '').toLowerCase(), (card.title || '').toLowerCase()].filter(Boolean),
                    isCustomCard: true,
                    isCustomTab: false
                };
            }
        });
        
        // Add custom tabs (filter-based custom cards)
        customTabs.forEach(tab => {
            const accessor = tab.accessor;
            const dataList = filterListData?.[accessor];
            if (dataList && dataList.length > 0) {
                availableFilters[accessor] = {
                    type: accessor,
                    label: tab.label || tab.title,
                    items: dataList,
                    selectedItems: cardFilter?.[accessor] || [],
                    aliases: [accessor, (tab.label || '').toLowerCase(), (tab.title || '').toLowerCase()].filter(Boolean),
                    isCustomCard: false,
                    isCustomTab: true
                };
            }
        });

        return availableFilters;
    }

    /**
     * Get Deceased page filters
     * @param {Object} filterListData - Filter list data from state
     * @param {Object} cardFilter - Card filter from state
     * @param {Object} pageState - Page state object
     * @returns {Object} Available filter options
     */
    getDeceasedFilters(filterListData, cardFilter, pageState) {
        const availableFilters = {
            // Insurance Card
            insurance: {
                type: DECEASED_CARDS_TYPE.INSURANCE_DATA,
                label: DECEASED_CARDS_LABELS.insuranceData,
                items: filterListData?.insuranceData || [],
                selectedItems: cardFilter?.insuranceData || [],
                aliases: ['insurance', 'insurances', 'payer', 'payers']
            },

            // Floors Card
            floors: {
                type: DECEASED_CARDS_TYPE.FLOORS_DATA,
                label: DECEASED_CARDS_LABELS.floorsData,
                items: filterListData?.floorsData || [],
                selectedItems: cardFilter?.floorsData || [],
                aliases: ['floor', 'floors', 'unit', 'units', 'ward', 'wards']
            },

            // Doctors Card
            doctors: {
                type: DECEASED_CARDS_TYPE.DOCTOR_DATA,
                label: DECEASED_CARDS_LABELS.doctorData,
                items: filterListData?.doctorData || [],
                selectedItems: cardFilter?.doctorData || [],
                aliases: ['doctor', 'doctors', 'physician', 'physicians', 'dr', 'doc']
            },

            // Days of Week Card
            days: {
                type: DECEASED_CARDS_TYPE.DAYS_DATA,
                label: DECEASED_CARDS_LABELS.daysData,
                items: filterListData?.daysData || [],
                selectedItems: cardFilter?.daysData || [],
                aliases: ['days', 'day', 'weekday', 'weekdays', 'day of week', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
            },

            // 90 Days Analysis Card
            ninetyDays: {
                type: DECEASED_CARDS_TYPE.NINETY_DAYS_DATA,
                label: DECEASED_CARDS_LABELS.ninetyDaysData,
                items: filterListData?.ninetyDaysData || [],
                selectedItems: cardFilter?.ninetyDaysData || [],
                aliases: ['90 days', 'ninety days', '90-days', 'days analysis', 'length of stay']
            },

            // Total Deceased Card
            total: {
                type: DECEASED_CARDS_TYPE.TOTAL_DATA,
                label: DECEASED_CARDS_LABELS.totalData,
                items: filterListData?.totalData || [],
                selectedItems: cardFilter?.totalData || [],
                aliases: ['deceased', 'total', 'mortality', 'death', 'died', 'passed away']
            }
        };

        // Add custom/dynamic cards
        const dynamicCards = pageState?.dynamicCards || [];
        const customTabs = pageState?.patientList?.customTabs || [];
        
        // Add dynamic cards (question-based custom cards)
        dynamicCards.forEach(card => {
            const accessor = card.accessor;
            const dataList = filterListData?.[accessor];
            if (dataList && dataList.length > 0) {
                availableFilters[accessor] = {
                    type: accessor,
                    label: card.label || card.title || card.tableLabel,
                    items: dataList,
                    selectedItems: cardFilter?.[accessor] || [],
                    aliases: [accessor, (card.label || '').toLowerCase(), (card.title || '').toLowerCase()].filter(Boolean),
                    isCustomCard: true,
                    isCustomTab: false
                };
            }
        });
        
        // Add custom tabs (filter-based custom cards)
        customTabs.forEach(tab => {
            const accessor = tab.accessor;
            const dataList = filterListData?.[accessor];
            if (dataList && dataList.length > 0) {
                availableFilters[accessor] = {
                    type: accessor,
                    label: tab.label || tab.title,
                    items: dataList,
                    selectedItems: cardFilter?.[accessor] || [],
                    aliases: [accessor, (tab.label || '').toLowerCase(), (tab.title || '').toLowerCase()].filter(Boolean),
                    isCustomCard: false,
                    isCustomTab: true
                };
            }
        });

        return availableFilters;
    }

    /**
     * Find card type by name or alias
     * @param {string} cardName - Card name or alias
     * @returns {Object|null} Card filter info
     */
    findCardByName(cardName) {
        if (!cardName || typeof cardName !== 'string') {
            console.warn('⚠️ findCardByName called with invalid cardName:', cardName);
            return null;
        }
        
        const filters = this.getAvailableFilters();
        const lowerName = cardName.toLowerCase().trim();

        // First try exact match on card keys
        if (filters[lowerName]) {
            return { key: lowerName, ...filters[lowerName] };
        }

        // Then try aliases
        for (const [key, filter] of Object.entries(filters)) {
            if (filter.aliases.some(alias => lowerName.includes(alias) || alias.includes(lowerName))) {
                return { key, ...filter };
            }
        }

        return null;
    }

    /**
     * Find specific item in a card by name
     * @param {Object} cardFilter - Card filter info
     * @param {string} itemName - Item name to find
     * @returns {Object|null} Found item
     */
    findItemInCard(cardFilter, itemName) {
        if (!cardFilter?.items || cardFilter.items.length === 0) return null;

        const lowerItemName = itemName.toLowerCase().trim();
        
        // Helper to get item display name (some cards use 'name', others use 'label')
        const getItemDisplayName = (item) => item.name || item.label || '';
        
        // Try exact match first (name or label)
        let foundItem = cardFilter.items.find(item => {
            const displayName = getItemDisplayName(item);
            return displayName && displayName.toLowerCase() === lowerItemName;
        });

        if (foundItem) return foundItem;

        // Try partial match (name or label)
        foundItem = cardFilter.items.find(item => {
            const displayName = getItemDisplayName(item);
            return displayName && (
                displayName.toLowerCase().includes(lowerItemName) ||
                lowerItemName.includes(displayName.toLowerCase())
            );
        });

        if (foundItem) return foundItem;

        // For numbered items like "doctor 1", "insurance 2", etc.
        const numberMatch = lowerItemName.match(/(\d+)/);
        if (numberMatch) {
            const index = parseInt(numberMatch[1]) - 1; // Convert to 0-based index
            if (index >= 0 && index < cardFilter.items.length) {
                return cardFilter.items[index];
            }
        }

        return null;
    }

    /**
     * Apply filter to a specific card item
     * @param {string} cardName - Card name (e.g., 'dc', 'doctors', 'insurance')
     * @param {string} itemName - Item name (e.g., 'DC', 'Doctor Smith', 'Medicare')
     * @returns {Promise<Object>} Result of filter application
     */
    async applyFilter(cardName, itemName = null) {
        try {
            logger.log('🔧 AI Filter Actions - applyFilter called:', { cardName, itemName });
            logger.log('🔧 AI Filter Actions - Service status:', {
                hasHandleToggle: !!this.handleToggle,
                hasHandleToggleAll: !!this.handleToggleAll,
                hasDispatch: !!this.dispatch,
                hasGetState: !!this.getState,
                hasOnFilterApplied: !!this.onFilterApplied
            });
            
            if (!this.handleToggle) {
                throw new Error('Filter actions not initialized - handleToggle is missing');
            }

            const cardFilter = this.findCardByName(cardName);
            logger.log('🔧 AI Filter Actions - found card filter:', cardFilter);
            
            if (!cardFilter) {
                return {
                    success: false,
                    error: `Card '${cardName}' not found. Available cards: ${Object.keys(this.getAvailableFilters()).join(', ')}`
                };
            }

            // If no specific item name provided, toggle all items in the card
            if (!itemName) {
                if (this.handleToggleAll) {
                    await this.handleToggleAll({
                        items: cardFilter.items,
                        type: cardFilter.type,
                        cardTitle: cardFilter.label
                    });

                    // Trigger UI refresh callback if provided
                    if (this.onFilterApplied) {
                        logger.log('🔄 Triggering UI refresh callback for toggle all');
                        this.onFilterApplied();
                    }

                    return {
                        success: true,
                        message: `Toggled all items in ${cardFilter.label}`,
                        cardType: cardFilter.type,
                        action: 'toggle_all'
                    };
                } else {
                    return {
                        success: false,
                        error: 'Toggle all function not available'
                    };
                }
            }

            // Find specific item in the card
            logger.log('🔧 AI Filter Actions - looking for item:', itemName, 'in card:', cardFilter.label);
            const item = this.findItemInCard(cardFilter, itemName);
            logger.log('🔧 AI Filter Actions - found item:', item);
            
            if (!item) {
                // Helper to get item display name (some cards use 'name', others use 'label')
                const getItemDisplayName = (item) => item.name || item.label || '';
                const availableItems = cardFilter.items.map(i => getItemDisplayName(i)).slice(0, 5).join(', ');
                return {
                    success: false,
                    error: `Item '${itemName}' not found in ${cardFilter.label}. Available items: ${availableItems}${cardFilter.items.length > 5 ? '...' : ''}`
                };
            }

            // Apply the filter using handleToggle
            logger.log('🔧 AI Filter Actions - calling handleToggle with:', {
                item: item,
                type: cardFilter.type,
                cardTitle: cardFilter.label
            });
            
            await this.handleToggle({
                item: item,
                type: cardFilter.type,
                cardTitle: cardFilter.label
            });

            // Get display name (some cards use 'name', others use 'label')
            const itemDisplayName = item.name || item.label || itemName;
            
            logger.log('🎯 AI Filter Applied successfully:', {
                cardName,
                itemName,
                cardType: cardFilter.type,
                itemId: item._id,
                itemDisplayName
            });

            // Trigger UI refresh callback if provided
            if (this.onFilterApplied) {
                logger.log('🔄 Triggering UI refresh callback');
                this.onFilterApplied();
            }

            return {
                success: true,
                message: `Filtered by ${itemDisplayName} in ${cardFilter.label}`,
                cardType: cardFilter.type,
                itemName: itemDisplayName,
                itemId: item._id,
                action: 'toggle_item'
            };

        } catch (error) {
            logger.error('❌ Error applying filter:', error);
            return {
                success: false,
                error: `Failed to apply filter: ${error.message}`
            };
        }
    }

    /**
     * Apply multiple filters from a single command
     * @param {Array} filterCommands - Array of {cardName, itemName} objects
     * @returns {Promise<Array>} Results of all filter applications
     */
    async applyMultipleFilters(filterCommands) {
        const results = [];

        for (const command of filterCommands) {
            const result = await this.applyFilter(command.cardName, command.itemName);
            results.push({
                ...result,
                command: command
            });
        }

        return results;
    }

    /**
     * Parse AI message to extract filter commands
     * @param {string} message - AI message like "apply filter to dc, doctor 1, insurance one"
     * @returns {Array} Parsed filter commands
     */
    parseFilterCommands(message) {
        const lowerMessage = message.toLowerCase();
        const commands = [];

        // Split by common separators
        const parts = lowerMessage.split(/[,;]|\s+and\s+|\s+then\s+/).map(part => part.trim());

        for (const part of parts) {
            // Remove common filter keywords
            const cleanPart = part.replace(/^(apply filter to|filter|add filter to|toggle|select)\s+/i, '').trim();
            
            if (!cleanPart) continue;

            // Check for numbered items like "doctor 1", "insurance 2"
            const numberedMatch = cleanPart.match(/^(\w+)\s+(\d+|one|two|three|four|five)$/i);
            if (numberedMatch) {
                const cardName = numberedMatch[1];
                const itemNumber = numberedMatch[2];
                
                // Convert word numbers to digits
                const wordToNumber = {
                    'one': '1', 'two': '2', 'three': '3', 'four': '4', 'five': '5'
                };
                const finalNumber = wordToNumber[itemNumber.toLowerCase()] || itemNumber;
                
                commands.push({
                    cardName: cardName,
                    itemName: `${cardName} ${finalNumber}`,
                    originalText: part
                });
                continue;
            }

            // Check for specific item names like "medicare", "medicaid", etc.
            const specificItemMatch = cleanPart.match(/^(\w+)\s+(.+)$/);
            if (specificItemMatch) {
                commands.push({
                    cardName: specificItemMatch[1],
                    itemName: specificItemMatch[2],
                    originalText: part
                });
                continue;
            }

            // Single card name (toggle all items)
            commands.push({
                cardName: cleanPart,
                itemName: null, // null means toggle all
                originalText: part
            });
        }

        return commands;
    }

    /**
     * Get current filter status for AI context
     * @returns {Object} Current filter status
     */
    getCurrentFilterStatus() {
        if (!this.getState) return {};

        const state = this.getState();
        const pageState = state[this.pageType] || state.hospital || state.communityTransfer || state.deceased || {};
        const { cardFilter } = pageState;
        const filters = this.getAvailableFilters();

        const status = {};
        for (const [key, filter] of Object.entries(filters)) {
            const selectedItems = cardFilter?.[filter.type] || [];
            status[key] = {
                label: filter.label,
                totalItems: filter.items.length,
                selectedCount: selectedItems.length,
                hasActiveFilters: selectedItems.length > 0,
                selectedItems: filter.items.filter(item => selectedItems.includes(item._id))
            };
        }

        return status;
    }

    /**
     * Get AI action suggestions for current state
     * @returns {Array} Suggested actions
     */
    getAIActionSuggestions() {
        const filterStatus = this.getCurrentFilterStatus();
        const suggestions = [];

        // Suggest clearing filters if many are active
        const activeFilters = Object.values(filterStatus).filter(f => f.hasActiveFilters);
        if (activeFilters.length > 2) {
            suggestions.push({
                type: 'clear_filters',
                description: `Clear ${activeFilters.length} active filters`,
                priority: 'high'
            });
        }

        // Suggest popular filters if none are active
        if (activeFilters.length === 0) {
            suggestions.push(
                { type: 'apply_filter', description: 'Filter by DC transfers', cardName: 'dc', itemName: 'DC' },
                { type: 'apply_filter', description: 'Filter by ER transfers', cardName: 'dc', itemName: 'ER' },
                { type: 'apply_filter', description: 'Filter by top doctor', cardName: 'doctors', itemName: null }
            );
        }

        return suggestions;
    }

    /**
     * Check if service is properly initialized
     * @returns {boolean} True if ready to execute actions
     */
    isInitialized() {
        return !!(this.dispatch && this.getState && this.handleToggle);
    }

    /**
     * Get debug info for troubleshooting
     * @returns {Object} Debug information
     */
    getDebugInfo() {
        return {
            initialized: this.isInitialized(),
            hasDispatch: !!this.dispatch,
            hasGetState: !!this.getState,
            hasHandleToggle: !!this.handleToggle,
            hasHandleToggleAll: !!this.handleToggleAll,
            hasOnFilterApplied: !!this.onFilterApplied,
            availableCards: Object.keys(this.getAvailableFilters()),
            currentFilterStatus: this.getCurrentFilterStatus()
        };
    }

    /**
     * Test function that can be called from console to test filter application
     * Usage: aiFilterActionsService.testDCFilter()
     */
    async testDCFilter() {
        logger.log('🧪 Testing DC filter application...');
        try {
            const result = await this.applyFilter('dc', 'DC');
            logger.log('🧪 Test result:', result);
            return result;
        } catch (error) {
            logger.error('🧪 Test failed:', error);
            return { success: false, error: error.message };
        }
    }
}

// Create singleton instance
const aiFilterActionsService = new AiFilterActionsService();

// Make available globally for testing in console
if (typeof window !== 'undefined') {
    window.aiFilterActionsService = aiFilterActionsService;
}

export default aiFilterActionsService;
